import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  ChevronRight,
  ChevronLeft,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  UserX,
  ShieldAlert,
  Clock3,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import type { SnapshotStatus as ApiSnapshotStatus } from '../../../api/PreformanceManagement/PerformanceSnapshot';

/**
 * SNAPSHOTS ANALYSIS — org-wide coverage read off the PERFORMANCE SNAPSHOT HISTORY.
 *
 * The question it answers: who has been through a performance check-up and who has not,
 * graded by how far gone the gap is. The records are PAST SNAPSHOTS, not check-ins.
 *
 * 🔴 THIS PANEL IS NOT WIRED TO THE BACKEND. `DEMO_EMPLOYEES` below is a generated fixture and
 * nothing here calls the API. That is deliberate and was explicitly asked for — the screen is a
 * demo of the shape, while the Module Config plumbing around it is real.
 *
 * 🔴 AND THE SCREEN DELIBERATELY DOES NOT SAY SO. The owner asked for the demo/mock badges to be
 * removed so it reads as a finished feature, which means THIS COMMENT is now the only warning a
 * future reader gets. Do not "fix" a backend that was never meant to exist yet, and do not delete
 * this note when wiring it up — replace it.
 *
 * WHEN WIRING IT FOR REAL: the history already exists server-side. `PerformanceSnapshotBase` holds
 * Period / OverallScore / Status / CreatedOn per employee, and
 * `PerformanceSnapshotController.GetEmployeeSnapshots` returns one employee's history (its client
 * wrapper `getEmployeeSnapshots()` exists and currently has no call sites). What is MISSING is an
 * org-wide coverage endpoint — only the per-employee route and the aggregate percentage inside
 * `Q12ScoringService` exist today. That endpoint is the real work, not this component.
 */

/** Never had a snapshot, or the last one predates the scoring window: the gap is total. */
const CRITICAL_AFTER_DAYS = 180;
/** The expected cadence. Missing last month is a warning, not yet a crisis. */
const OVERDUE_AFTER_DAYS = 30;

/* ──────────────────────────────────────────────────────────────────────────────────────────────
   THE COVERAGE SCORE — owner spec 2026-08-24.

   🔴 IT IS NOT A PERFORMANCE SCORE AND IT IS NOT SHOWN PER PERSON. The old table carried a
   per-row "Score" column reading the snapshot's own `OverallScore`, which put an individual's
   performance number on an HR triage screen next to their name. That column is GONE. What
   replaces it is computed in the background and only ever surfaces AGGREGATED — company-wide and
   per department — in "Where the gap sits". Do not render `coverageScore` on a row.

   The rule, exactly as specified:
     • An employee who HAS had a performance snapshot in the last 6 months sits at 100%. Getting a
       snapshot RESETS that person to 100% — the clock restarts from their newest snapshot, no
       memory of how long the previous gap was.
     • Past 6 consecutive months without one, the score DEGRADES. Linear from the 6-month mark,
       8.33 points per further month, so a person untouched for 18 months contributes 0.
     • Never had one at all = 0. There is nothing to decay from.

   Company and department scores are the MEAN of their members' scores, which makes them read as
   "how much of this group is currently covered" rather than as anyone's performance.
   ────────────────────────────────────────────────────────────────────────────────────────────── */

/** Days of grace before the score starts falling. Same 6 months the severity grading uses. */
const SCORE_GRACE_DAYS = CRITICAL_AFTER_DAYS;
/** Days from the end of grace to a score of zero — one further year. */
const SCORE_DECAY_DAYS = 365;

type Severity = 'critical' | 'overdue' | 'covered';

/**
 * CO-429 — the status is a STRING on the wire, not a number. This panel is a UI-only demo and does
 * not call the API, but it typed its fixture as `1 | 2 | 3` and keyed `STATUS_KEY` off those
 * numbers, which is exactly the mismatch that made the real page's action bar never render. The
 * type is corrected here so the hazard cannot be copied out of this file when the panel is finally
 * wired to the org-wide coverage endpoint. Nothing else about the demo changes.
 */
type SnapshotStatus = ApiSnapshotStatus;

interface SnapshotRecord {
  id: string;
  /** "2026-Q1" or "2026-03", exactly as the entity stores it. */
  period: string;
  /** Days ago the snapshot was created. */
  daysAgo: number;
  /** 0-100, or null when the snapshot was saved before scoring completed. */
  score: number | null;
  status: SnapshotStatus;
}

interface EmployeeCoverage {
  id: string;
  name: string;
  department: string;
  manager: string;
  /** Newest first. Empty = never had a performance snapshot at all. */
  snapshots: SnapshotRecord[];
}

/* ──────────────────────────────────────────────────────────────────────────────────────────────
   THE FIXTURE.

   Generated rather than hand-listed, and deliberately LARGE - 420 rows across a dozen departments
   and ten managers. The table below has to survive a real company, and the 22-row fixture it
   replaces could not show whether it does: paging, sorting and filtering all look fine on 22 rows.
   420 is enough to exercise every one of them (17 pages at the default size) while staying cheap
   to render; it is not a claim that the screen has been proven at 2,000. The generator is fully
   deterministic (a seeded LCG, no `Math.random`) so the screen is identical on every render and
   every machine, which is what makes it demoable and screenshot-stable.
   ────────────────────────────────────────────────────────────────────────────────────────────── */

const DEPARTMENTS = [
  'Engineering', 'Sales', 'Finance', 'Operations', 'People', 'Marketing',
  'Customer Success', 'Legal', 'Procurement', 'IT Support', 'Quality', 'Logistics',
];

const MANAGERS = [
  'Omar Nasser', 'Hala Mansour', 'Rami Deeb', 'Layal Kassem', 'Ibrahim Saad',
  'Nadia Sleiman', 'Tarek Amine', 'Rita Khoury', 'Ali Chahine', 'Maha Zeidan',
];

const FIRST_NAMES = [
  'Yara', 'Karim', 'Lina', 'Tarek', 'Nour', 'Ziad', 'Maya', 'Fadi', 'Rana', 'Sami',
  'Dina', 'Hadi', 'Jana', 'Bassel', 'Salma', 'Wassim', 'Mira', 'Elie', 'Rima', 'Georges',
  'Aya', 'Marwan', 'Leila', 'Kamal', 'Hiba', 'Nabil', 'Sara', 'Joseph', 'Reem', 'Amir',
];

const LAST_NAMES = [
  'Haddad', 'Fahmy', 'Sabbagh', 'Zayed', 'Khalil', 'Barakat', 'Chalhoub', 'Aoun', 'Ghanem',
  'Hourani', 'Abboud', 'Srour', 'Mrad', 'Rizk', 'Turk', 'Daher', 'Kanaan', 'Matar', 'Ayoub',
  'Helou', 'Darwish', 'Itani', 'Nakhle', 'Sfeir', 'Jaber', 'Bitar', 'Antoun', 'Farah',
];

const STATUSES: SnapshotStatus[] = ['Draft', 'Submitted', 'Acknowledged'];

/** Deterministic pseudo-random — a plain LCG, so the fixture never shifts between renders. */
function makeRng(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

/** `daysAgo` → the `yyyy-MM` month that snapshot would have been written in. */
function periodFor(daysAgo: number): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - Math.floor(daysAgo / 30));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function buildDemoRoster(count: number): EmployeeCoverage[] {
  const rng = makeRng(20260824);
  const roster: EmployeeCoverage[] = [];

  for (let i = 0; i < count; i += 1) {
    const first = FIRST_NAMES[Math.floor(rng() * FIRST_NAMES.length)];
    const last = LAST_NAMES[Math.floor(rng() * LAST_NAMES.length)];
    const department = DEPARTMENTS[Math.floor(rng() * DEPARTMENTS.length)];
    // Managers cluster per department rather than being sprayed across the company, so the
    // manager filter has the shape it would really have.
    const manager = MANAGERS[(DEPARTMENTS.indexOf(department) + Math.floor(rng() * 2)) % MANAGERS.length];

    const roll = rng();
    let snapshots: SnapshotRecord[] = [];

    if (roll < 0.14) {
      // Never had one.
      snapshots = [];
    } else {
      // Where the most recent snapshot sits. Weighted so most people are current, a slice are
      // drifting, and a tail is badly gone — the distribution the triage screen exists to show.
      const recencyRoll = rng();
      const latestDaysAgo =
        recencyRoll < 0.5 ? Math.floor(rng() * 30)
          : recencyRoll < 0.78 ? 30 + Math.floor(rng() * 150)
            : recencyRoll < 0.92 ? 181 + Math.floor(rng() * 180)
              : 361 + Math.floor(rng() * 420);

      const historyLength = 1 + Math.floor(rng() * 4);
      let cursor = latestDaysAgo;
      for (let s = 0; s < historyLength; s += 1) {
        snapshots.push({
          id: `${i}-${s}`,
          period: periodFor(cursor),
          daysAgo: cursor,
          // A small share of rows carry no score, exactly as a snapshot saved before scoring
          // completed does. The value is never rendered on a row any more, but it is still what
          // the real record holds and the fixture should not lie about the shape.
          score: rng() < 0.06 ? null : 52 + Math.floor(rng() * 46),
          status: s === 0 ? STATUSES[Math.floor(rng() * 3)] : 'Acknowledged',
        });
        cursor += 28 + Math.floor(rng() * 40);
      }
    }

    roster.push({ id: String(i + 1), name: `${first} ${last}`, department, manager, snapshots });
  }

  return roster;
}

const DEMO_EMPLOYEES: EmployeeCoverage[] = buildDemoRoster(420);

/** The newest snapshot, or null when the employee has never had one. */
function latestOf(employee: EmployeeCoverage): SnapshotRecord | null {
  return employee.snapshots.length > 0 ? employee.snapshots[0] : null;
}

function severityOf(employee: EmployeeCoverage): Severity {
  const latest = latestOf(employee);
  if (latest === null || latest.daysAgo > CRITICAL_AFTER_DAYS) return 'critical';
  if (latest.daysAgo > OVERDUE_AFTER_DAYS) return 'overdue';
  return 'covered';
}

/**
 * The per-employee coverage score, 0-100. Background input to the aggregates only — see the spec
 * block above for why it is never rendered next to a name.
 */
function coverageScoreOf(employee: EmployeeCoverage): number {
  const latest = latestOf(employee);
  if (latest === null) return 0;
  // Any snapshot inside the window puts the person back at full coverage. This is the "resets to
  // 100%" rule, and it works simply by reading the NEWEST snapshot and nothing else.
  if (latest.daysAgo <= SCORE_GRACE_DAYS) return 100;
  const overdueDays = latest.daysAgo - SCORE_GRACE_DAYS;
  // FLOOR, not round. `Math.round` reported day 181 - someone the severity grading already calls
  // CRITICAL - as a clean 100%, so "100% covered" could be true on a screen simultaneously showing
  // people in the red. 100 now means exactly what it says: nobody is past the six-month mark.
  return Math.max(0, Math.floor(100 - (overdueDays / SCORE_DECAY_DAYS) * 100));
}

const mean = (values: number[]) =>
  values.length === 0 ? 0 : Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);

/**
 * Severity presentation in one place, so the proportion bar, the legend, the filters and the row
 * chips can never drift apart. Semantic state colour only, never decoration.
 */
const SEVERITY_STYLE: Record<Severity, { chip: string; bar: string; dot: string; icon: typeof ShieldAlert }> = {
  critical: {
    // Palette red, NOT the `destructive` token: alpha modifiers on the var()-based theme tokens
    // emit nothing in this build, which left the most important severity as the only untinted one.
    chip: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
    bar: 'bg-red-500',
    dot: 'bg-red-500',
    icon: ShieldAlert,
  },
  overdue: {
    chip: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
    bar: 'bg-amber-500',
    dot: 'bg-amber-500',
    icon: Clock3,
  },
  covered: {
    chip: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
    bar: 'bg-emerald-500',
    dot: 'bg-emerald-500',
    icon: CheckCircle2,
  },
};

const SEVERITY_ORDER: Severity[] = ['critical', 'overdue', 'covered'];
const STATUS_KEY: Record<SnapshotStatus, string> = { Draft: 'draft', Submitted: 'submitted', Acknowledged: 'acknowledged' };

/** Recency buckets, as a filter. Complements severity — "who fell off in the last quarter". */
type RecencyBucket = 'all' | 'never' | 'over12m' | 'over6m' | 'over3m' | 'under1m';

const RECENCY_BUCKETS: RecencyBucket[] = ['all', 'under1m', 'over3m', 'over6m', 'over12m', 'never'];

function matchesRecency(bucket: RecencyBucket, latest: SnapshotRecord | null): boolean {
  if (bucket === 'all') return true;
  if (bucket === 'never') return latest === null;
  if (latest === null) return false;
  switch (bucket) {
    case 'under1m': return latest.daysAgo <= 30;
    case 'over3m': return latest.daysAgo > 90;
    case 'over6m': return latest.daysAgo > 180;
    case 'over12m': return latest.daysAgo > 365;
    default: return true;
  }
}

type SortKey = 'name' | 'department' | 'manager' | 'lastSnapshot' | 'history' | 'severity';
type SortDirection = 'asc' | 'desc';

/** Owner decision 2026-08-24: a short page by default. The first entry is the default. */
const PAGE_SIZES = [10, 25, 50, 100];

/** Colour the aggregate percentages by how bad they are — the same three states as everything else. */
function scoreTone(score: number): Severity {
  if (score >= 80) return 'covered';
  if (score >= 50) return 'overdue';
  return 'critical';
}

interface SnapshotsAnalysisPanelProps {
  /**
   * Open the employee section for a row. UI-ONLY FOR NOW, and the caller knows it: the fixture's
   * ids are invented and match nothing on the roster, so the parent selects a real employee of its
   * own choosing rather than looking this id up. That is what was asked for while the panel is a
   * demo — when the org-wide endpoint lands, the id becomes real and this becomes a lookup.
   */
  onOpenEmployeeSnapshot?: (employeeId: string) => void;
}

const SnapshotsAnalysisPanel = ({ onOpenEmployeeSnapshot }: SnapshotsAnalysisPanelProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('all');
  const [manager, setManager] = useState('all');
  const [recency, setRecency] = useState<RecencyBucket>('all');
  const [statusFilter, setStatusFilter] = useState<SnapshotStatus | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<Severity | 'all'>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('severity');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  const graded = useMemo(
    () => DEMO_EMPLOYEES.map(employee => ({
      ...employee,
      severity: severityOf(employee),
      latest: latestOf(employee),
      coverageScore: coverageScoreOf(employee),
    })),
    []
  );

  const counts = useMemo(() => {
    const base: Record<Severity, number> = { critical: 0, overdue: 0, covered: 0 };
    graded.forEach(row => { base[row.severity] += 1; });
    return base;
  }, [graded]);

  const departments = useMemo(
    () => Array.from(new Set(DEMO_EMPLOYEES.map(e => e.department))).sort(),
    []
  );

  const managers = useMemo(
    () => Array.from(new Set(DEMO_EMPLOYEES.map(e => e.manager))).sort(),
    []
  );

  /** The company-wide coverage score — the mean of every employee's background score. */
  const companyScore = useMemo(() => mean(graded.map(row => row.coverageScore)), [graded]);

  /**
   * Where the gap sits: EVERY department, scored, worst first. This is the ONLY place a percentage
   * is allowed to appear (owner decision) — the summary bar counts people and the table shows
   * dates and states, never a number out of 100.
   *
   * 🔴 THE COUNT BESIDE THE SCORE COUNTS THE SAME PEOPLE THE SCORE IS MADE OF. It first counted
   * `severity !== 'covered'`, which is everyone past the 30-DAY cadence, while the score only
   * starts falling at SIX MONTHS. The two therefore measured different populations, and the row
   * read as a contradiction: a department could be ranked healthiest on score while carrying the
   * largest count next to it, and ranking by score put the worst-count department at the top of
   * the healthy end - the exact opposite of the triage this section exists for. The count is now
   * "people below full coverage", i.e. `coverageScore < 100`, which is precisely what drags the
   * percentage down. Number and label now tell one story, and the ranking follows it.
   */
  const concentration = useMemo(() => {
    const byDept = new Map<string, { scores: number[]; gap: number }>();
    graded.forEach(row => {
      const entry = byDept.get(row.department) ?? { scores: [], gap: 0 };
      entry.scores.push(row.coverageScore);
      if (row.coverageScore < 100) entry.gap += 1;
      byDept.set(row.department, entry);
    });
    return Array.from(byDept.entries())
      .map(([name, entry]) => ({
        name,
        score: mean(entry.scores),
        gap: entry.gap,
        headcount: entry.scores.length,
      }))
      .sort((a, b) => a.score - b.score || b.gap - a.gap);
  }, [graded]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return graded
      .filter(row => severityFilter === 'all' || row.severity === severityFilter)
      .filter(row => department === 'all' || row.department === department)
      .filter(row => manager === 'all' || row.manager === manager)
      .filter(row => matchesRecency(recency, row.latest))
      .filter(row => statusFilter === 'all' || row.latest?.status === statusFilter)
      .filter(row => !needle
        || row.name.toLowerCase().includes(needle)
        || row.manager.toLowerCase().includes(needle)
        || row.department.toLowerCase().includes(needle));
  }, [graded, search, department, manager, recency, statusFilter, severityFilter]);

  const sorted = useMemo(() => {
    const factor = sortDirection === 'asc' ? 1 : -1;
    // A never-reviewed employee sorts as infinitely stale, never as "0 days ago".
    const staleness = (row: typeof graded[number]) => row.latest?.daysAgo ?? Number.MAX_SAFE_INTEGER;
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'name': return factor * a.name.localeCompare(b.name);
        case 'department': return factor * (a.department.localeCompare(b.department) || a.name.localeCompare(b.name));
        case 'manager': return factor * (a.manager.localeCompare(b.manager) || a.name.localeCompare(b.name));
        case 'history': return factor * (a.snapshots.length - b.snapshots.length) || a.name.localeCompare(b.name);
        case 'lastSnapshot': return factor * (staleness(a) - staleness(b));
        case 'severity':
        default: {
          const bySeverity = SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity);
          if (bySeverity !== 0) return factor * bySeverity;
          return factor * (staleness(b) - staleness(a));
        }
      }
    });
  }, [filtered, sortKey, sortDirection]);

  const total = graded.length;
  const covered = counts.covered;
  const hasFilters =
    search.trim() !== '' || department !== 'all' || manager !== 'all'
    || recency !== 'all' || statusFilter !== 'all' || severityFilter !== 'all';

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  // Narrowing the filters must not strand the reader on a page that no longer exists.
  useEffect(() => { setPage(0); }, [search, department, manager, recency, statusFilter, severityFilter, pageSize]);
  const safePage = Math.min(page, pageCount - 1);
  const rows = useMemo(
    () => sorted.slice(safePage * pageSize, safePage * pageSize + pageSize),
    [sorted, safePage, pageSize]
  );

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setDepartment('all');
    setManager('all');
    setRecency('all');
    setStatusFilter('all');
    setSeverityFilter('all');
  };

  const sinceLabel = (days: number) =>
    days < 1 ? t('performanceSnapshot.analysis.today')
      : days < 30 ? t('performanceSnapshot.analysis.daysAgo', { count: days })
        : days < 365 ? t('performanceSnapshot.analysis.monthsAgo', { count: Math.round(days / 30) })
          : t('performanceSnapshot.analysis.yearsAgo', { count: Math.round(days / 365) });

  const selectClass =
    'py-2 px-3 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';

  /**
   * A sortable header cell. The icon states which column is driving the order, and which way.
   *
   * A FUNCTION THAT RETURNS JSX, deliberately not a component. Declared as `const SortHeader = …`
   * and used as `<SortHeader/>`, it would be a NEW COMPONENT TYPE on every render of this panel,
   * so React would unmount and remount all six `<th>` subtrees on every keystroke in the search
   * box - throwing away focus on whichever sort button the keyboard user was on. Called as a
   * plain function the JSX is simply inlined and nothing remounts.
   */
  const sortHeader = (column: SortKey, label: string, className = '') => {
    const active = sortKey === column;
    const Icon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ChevronsUpDown;
    return (
      <th
        key={column}
        className={`py-3 font-medium text-muted-foreground text-start ${className}`}
        aria-sort={active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <button
          type="button"
          onClick={() => toggleSort(column)}
          className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors duration-200"
        >
          <span>{label}</span>
          <Icon size={13} className={active ? 'text-foreground' : 'opacity-50'} aria-hidden="true" />
        </button>
      </th>
    );
  };

  return (
    <div className="space-y-6">
      {/* Coverage — a single proportion bar reads faster than three stat tiles, and the legend
          doubles as the severity filter.

          The bar and its counts are deliberately over the WHOLE roster, never the filtered set:
          they are the thing being filtered, and a legend that reacted to its own selection would
          collapse to one segment the moment it was used. The table's own result count below says
          how many rows the current filters match. */}
      <section className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
          <h3 className="text-lg font-semibold text-foreground">
            {t('performanceSnapshot.analysis.coverageTitle')}
          </h3>
          <p className="text-sm text-muted-foreground tabular-nums">
            {t('performanceSnapshot.analysis.coverageSummary', { covered, total })}
          </p>
        </div>

        <div
          className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label={t('performanceSnapshot.analysis.coverageSummary', { covered, total })}
        >
          {SEVERITY_ORDER.map(severity => (
            counts[severity] > 0 && (
              <div
                key={severity}
                className={SEVERITY_STYLE[severity].bar}
                style={{ width: `${(counts[severity] / total) * 100}%` }}
              />
            )
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSeverityFilter('all')}
            aria-pressed={severityFilter === 'all'}
            className={`px-3 py-1.5 rounded-md border text-sm font-medium transition-colors duration-200 ${
              severityFilter === 'all'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            {t('performanceSnapshot.analysis.filterAll', { count: total })}
          </button>
          {SEVERITY_ORDER.map(severity => {
            const active = severityFilter === severity;
            return (
              <button
                key={severity}
                type="button"
                onClick={() => setSeverityFilter(active ? 'all' : severity)}
                aria-pressed={active}
                data-testid={`analysis-filter-${severity}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors duration-200 ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${active ? 'bg-primary-foreground' : SEVERITY_STYLE[severity].dot}`} />
                <span>{t(`performanceSnapshot.analysis.severity.${severity}`)}</span>
                <span className="tabular-nums opacity-70">{counts[severity]}</span>
              </button>
            );
          })}
        </div>

        <p className="mt-4 text-sm text-muted-foreground max-w-[70ch]">
          {t('performanceSnapshot.analysis.severityExplainer')}
        </p>
      </section>

      {/* Where the gap sits. THE ONLY PLACE PERCENTAGES APPEAR — company-wide first, then every
          department scored and ranked worst-first. Ranked bars, not a card grid. */}
      {concentration.length > 0 && (
        <section className="bg-card border border-border rounded-lg p-6" data-testid="analysis-concentration">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
            <h3 className="text-lg font-semibold text-foreground">
              {t('performanceSnapshot.analysis.concentrationTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('performanceSnapshot.analysis.companyScoreLabel')}{' '}
              <span
                data-testid="analysis-company-score"
                className={`font-semibold tabular-nums ${
                  scoreTone(companyScore) === 'covered' ? 'text-emerald-600 dark:text-emerald-400'
                    : scoreTone(companyScore) === 'overdue' ? 'text-amber-600 dark:text-amber-400'
                      : 'text-red-600 dark:text-red-400'
                }`}
              >
                {companyScore}%
              </span>
            </p>
          </div>
          <p className="text-sm text-muted-foreground mb-5 max-w-[70ch]">
            {t('performanceSnapshot.analysis.scoreExplainer')}
          </p>
          <ul className="space-y-3">
            {concentration.map(entry => (
              <li key={entry.name} className="flex items-center gap-4">
                <span className="w-40 shrink-0 text-sm text-foreground truncate" title={entry.name}>{entry.name}</span>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full ${SEVERITY_STYLE[scoreTone(entry.score)].bar}`}
                    style={{ width: `${entry.score}%` }}
                  />
                </div>
                <span className="w-12 shrink-0 text-end text-sm font-semibold text-foreground tabular-nums">
                  {entry.score}%
                </span>
                <span className="w-28 shrink-0 text-end text-xs text-muted-foreground tabular-nums">
                  {t('performanceSnapshot.analysis.gapOfHeadcount', { gap: entry.gap, headcount: entry.headcount })}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Snapshot history. Each row is an employee; expanding reveals their PAST SNAPSHOTS. */}
      <section className="bg-card border border-border rounded-lg">
        <div className="p-6 pb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-semibold text-foreground me-auto">
              {t('performanceSnapshot.analysis.historyTitle')}
            </h3>
            <div className="relative">
              <Search
                size={16}
                className="absolute top-1/2 -translate-y-1/2 text-muted-foreground ltr:left-3 rtl:right-3"
              />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('performanceSnapshot.analysis.searchPlaceholder')}
                aria-label={t('performanceSnapshot.analysis.searchPlaceholder')}
                className="w-56 py-2 ps-9 pe-3 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select
              value={department}
              onChange={e => setDepartment(e.target.value)}
              aria-label={t('performanceSnapshot.analysis.departmentFilter')}
              className={selectClass}
            >
              <option value="all">{t('performanceSnapshot.analysis.allDepartments')}</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select
              value={manager}
              onChange={e => setManager(e.target.value)}
              aria-label={t('performanceSnapshot.analysis.managerFilter')}
              className={selectClass}
            >
              <option value="all">{t('performanceSnapshot.analysis.allManagers')}</option>
              {managers.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select
              value={recency}
              onChange={e => setRecency(e.target.value as RecencyBucket)}
              aria-label={t('performanceSnapshot.analysis.recencyFilter')}
              className={selectClass}
            >
              {RECENCY_BUCKETS.map(bucket => (
                <option key={bucket} value={bucket}>
                  {t(`performanceSnapshot.analysis.recency.${bucket}`)}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as SnapshotStatus | 'all')}
              aria-label={t('performanceSnapshot.analysis.statusFilter')}
              className={selectClass}
            >
              <option value="all">{t('performanceSnapshot.analysis.allStatuses')}</option>
              {STATUSES.map(s => (
                <option key={s} value={s}>{t(`performanceSnapshot.analysis.status.${STATUS_KEY[s]}`)}</option>
              ))}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                {t('performanceSnapshot.analysis.clearFilters')}
              </button>
            )}
            <span className="ms-auto text-sm text-muted-foreground tabular-nums" data-testid="analysis-result-count">
              {t('performanceSnapshot.analysis.matchCount', { count: sorted.length })}
            </span>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="px-6 pb-10 pt-4 text-center">
            <UserX size={28} className="mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-sm text-muted-foreground">
              {t('performanceSnapshot.analysis.noMatches')}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y border-border bg-muted/30 text-start">
                    {sortHeader('name', t('performanceSnapshot.analysis.col.employee'), 'px-6')}
                    {sortHeader('department', t('performanceSnapshot.analysis.col.department'), 'px-4')}
                    {sortHeader('manager', t('performanceSnapshot.analysis.col.manager'), 'px-4')}
                    {sortHeader('lastSnapshot', t('performanceSnapshot.analysis.col.lastSnapshot'), 'px-4')}
                    {sortHeader('history', t('performanceSnapshot.analysis.col.history'), 'px-4')}
                    {sortHeader('severity', t('performanceSnapshot.analysis.col.status'), 'px-4')}
                    {/* `relative`, and it is load-bearing. Tailwind's `sr-only` is
                        `position:absolute`, and with no positioned ancestor the span resolved
                        against the INITIAL containing block - so it escaped <main>'s
                        `overflow-y:auto` clip and stretched the DOCUMENT scroll area to the
                        table's full height. The page then showed TWO vertical scrollbars: the
                        real one on <main> and a phantom one on <html>. Positioning the cell
                        keeps the hidden label inside the scroll container that clips it. */}
                    <th className="relative px-4 py-3 font-medium text-muted-foreground text-end">
                      <span className="sr-only">{t('performanceSnapshot.analysis.col.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => {
                    const style = SEVERITY_STYLE[row.severity];
                    const SeverityIcon = style.icon;
                    const isOpen = expanded === row.id;
                    const hasHistory = row.snapshots.length > 0;
                    return [
                      <tr
                        key={row.id}
                        className={`border-b border-border last:border-0 ${hasHistory ? 'cursor-pointer hover:bg-muted' : ''} transition-colors duration-200`}
                        onClick={hasHistory ? () => setExpanded(isOpen ? null : row.id) : undefined}
                        onKeyDown={hasHistory ? (event) => {
                          // ONLY when the ROW itself has focus. Without this guard, Enter pressed on
                          // the "View details" button bubbled to here, got `preventDefault`d - which
                          // cancels the button's own activation - and toggled the expander instead,
                          // leaving the details action unreachable by keyboard entirely.
                          if (event.target !== event.currentTarget) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setExpanded(isOpen ? null : row.id);
                          }
                        } : undefined}
                        tabIndex={hasHistory ? 0 : undefined}
                        role={hasHistory ? 'button' : undefined}
                        aria-expanded={hasHistory ? isOpen : undefined}
                        aria-label={hasHistory
                          ? t('performanceSnapshot.analysis.toggleHistory', { name: row.name })
                          : undefined}
                      >
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            {hasHistory ? (
                              <ChevronRight
                                size={14}
                                // The two states are MUTUALLY EXCLUSIVE class sets, not one set
                                // overriding the other. `rtl:rotate-180 ... rtl:rotate-90` left both
                                // variants live in Arabic and the sheet order decided the winner, so
                                // expanding a row produced no chevron movement at all in RTL.
                                className={`text-muted-foreground transition-transform duration-200 ${
                                  isOpen ? 'rotate-90' : 'rtl:rotate-180'
                                }`}
                                aria-hidden="true"
                              />
                            ) : (
                              <span className="w-[14px]" aria-hidden="true" />
                            )}
                            <span className="font-medium text-foreground">{row.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{row.department}</td>
                        <td className="px-4 py-3 text-muted-foreground">{row.manager}</td>
                        <td className="px-4 py-3">
                          {row.latest ? (
                            <div>
                              <div className="text-foreground tabular-nums" dir="ltr">{row.latest.period}</div>
                              <div className="text-xs text-muted-foreground">{sinceLabel(row.latest.daysAgo)}</div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">
                              {t('performanceSnapshot.analysis.never')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-muted-foreground">
                          {t('performanceSnapshot.analysis.snapshotCount', { count: row.snapshots.length })}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium ${style.chip}`}>
                            <SeverityIcon size={12} />
                            {t(`performanceSnapshot.analysis.severity.${row.severity}`)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-end">
                          <button
                            type="button"
                            // The row itself toggles history; the button must not do both.
                            onClick={(event) => { event.stopPropagation(); onOpenEmployeeSnapshot?.(row.id); }}
                            data-testid="analysis-open-details"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs font-medium text-foreground hover:bg-muted transition-colors duration-200 whitespace-nowrap"
                          >
                            <span>{t('performanceSnapshot.analysis.viewDetails')}</span>
                            <ExternalLink size={12} aria-hidden="true" />
                          </button>
                        </td>
                      </tr>,
                      isOpen && (
                        <tr key={`${row.id}-history`} className="border-b border-border bg-muted">
                          <td colSpan={7} className="px-6 py-4">
                            <p className="text-xs font-medium text-muted-foreground mb-3">
                              {t('performanceSnapshot.analysis.previousSnapshots')}
                            </p>
                            <ul className="space-y-2">
                              {row.snapshots.map(snapshot => (
                                <li key={snapshot.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                                  <span className="w-20 font-medium text-foreground tabular-nums" dir="ltr">{snapshot.period}</span>
                                  <span className="w-28 text-muted-foreground">{sinceLabel(snapshot.daysAgo)}</span>
                                  <span className="text-muted-foreground">
                                    {t(`performanceSnapshot.analysis.status.${STATUS_KEY[snapshot.status]}`)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                </tbody>
              </table>
            </div>

            {/* Paging. A real roster is thousands of rows; the table renders one page of them. */}
            <div className="flex flex-wrap items-center gap-3 px-6 py-4 border-t border-border">
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{t('performanceSnapshot.analysis.rowsPerPage')}</span>
                <select
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                  className={selectClass}
                >
                  {PAGE_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
                </select>
              </label>
              <span className="ms-auto text-sm text-muted-foreground tabular-nums">
                {t('performanceSnapshot.analysis.pageOf', { page: safePage + 1, pages: pageCount })}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  aria-label={t('performanceSnapshot.analysis.previousPage')}
                  className="p-2 rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  <ChevronLeft size={16} className="rtl:rotate-180" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                  disabled={safePage >= pageCount - 1}
                  aria-label={t('performanceSnapshot.analysis.nextPage')}
                  className="p-2 rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  <ChevronRight size={16} className="rtl:rotate-180" aria-hidden="true" />
                </button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default SnapshotsAnalysisPanel;
